import { NAV_GROUPS, ROUTE_META, type RouteGroup, type RouteMeta } from "@/app/routeRegistry";

export type NavigationGroups = Readonly<Record<string, readonly string[]>>;

const canonicalByGroup = new Map<RouteGroup, RouteMeta[]>(
  NAV_GROUPS.map((group) => [group.id, ROUTE_META.filter((route) => route.nav && route.group === group.id)]),
);
const routeGroup = new Map(ROUTE_META.filter((route) => route.nav).map((route) => [route.id, route.group]));

function completeOrder(group: RouteGroup, stored: readonly string[] | undefined): string[] {
  const canonical = (canonicalByGroup.get(group) ?? []).map((route) => route.id);
  if (!stored) return canonical;
  if (new Set(stored).size !== stored.length) return canonical;
  if (stored.some((id) => routeGroup.has(id) && routeGroup.get(id) !== group)) return canonical;
  const known = stored.filter((id) => routeGroup.get(id) === group);
  return [...known, ...canonical.filter((id) => !known.includes(id))];
}

/** capability filtering 뒤의 route만 반환하며 저장값이 숨은 route를 되살릴 수 없습니다. */
export function orderVisibleRoutes(
  group: RouteGroup,
  visible: readonly RouteMeta[],
  groups: NavigationGroups | undefined,
): RouteMeta[] {
  const byId = new Map(visible.map((route) => [route.id, route]));
  return completeOrder(group, groups?.[group]).flatMap((id) => {
    const route = byId.get(id);
    return route ? [route] : [];
  });
}

/** 보이는 이웃끼리만 교환하되 숨은 route의 고정 그룹 소속과 상대 위치는 보존합니다. */
export function moveVisibleRoute(
  group: RouteGroup,
  visible: readonly RouteMeta[],
  groups: NavigationGroups | undefined,
  routeId: string,
  delta: -1 | 1,
): NavigationGroups {
  const visibleIds = orderVisibleRoutes(group, visible, groups).map((route) => route.id);
  const index = visibleIds.indexOf(routeId);
  const other = visibleIds[index + delta];
  const full = completeOrder(group, groups?.[group]);
  if (index < 0 || !other) return groups ?? {};
  const from = full.indexOf(routeId);
  const to = full.indexOf(other);
  [full[from], full[to]] = [full[to], full[from]];
  return { ...(groups ?? {}), [group]: full };
}

export function shortcutRoutes(groups: NavigationGroups | undefined, visibleByGroup: ReadonlyMap<RouteGroup, readonly RouteMeta[]>): RouteMeta[] {
  return NAV_GROUPS.flatMap((group) => orderVisibleRoutes(group.id, visibleByGroup.get(group.id) ?? [], groups)).slice(0, 9);
}
